import * as Icons from "lucide-react";
import { fmtVal } from "./reviewMeta";

// model = { autoUpdates, proposedUpdates, pendingUpdates, acceptedUpdates }
// actions = { onAccept, onReject }
export function RecordUpdatesSection({ model, actions }) {
  const { autoUpdates, proposedUpdates, pendingUpdates, acceptedUpdates } = model;
  const dismissedCount = proposedUpdates.filter((p) => p.status === "rejected").length;
  const reasonLabel = { conflict: "Conflicts with existing value", inferred: "Allison inferred this", decision_field: "Decision field — needs confirmation" };
  return (
    <div data-testid="ca-record-updates">
      <div className="grid grid-cols-4 gap-2 mb-5">
        {[["Auto Applied", autoUpdates.length, "emerald"], ["Require Review", pendingUpdates.length, "amber"], ["Accepted", acceptedUpdates.length, "blue"], ["Dismissed", dismissedCount, "slate"]].map(([l, v, tone]) => (
          <div key={l} className={`rounded-lg border px-3 py-2 ${tone === "amber" ? "bg-amber-50 border-amber-200" : tone === "emerald" ? "bg-emerald-50 border-emerald-200" : tone === "blue" ? "bg-blue-50 border-blue-200" : "bg-slate-50 border-slate-200"}`}>
            <div className="text-2xl font-bold text-slate-800">{v}</div>
            <div className="text-[11px] text-slate-500">{l}</div>
          </div>
        ))}
      </div>
      {autoUpdates.length > 0 && (
        <div className="mb-5" data-testid="ca-updates-auto">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-emerald-600 mb-2 flex items-center gap-1.5"><Icons.CheckCircle2 className="h-3.5 w-3.5" />Auto-Applied Low-Risk Updates ({autoUpdates.length})</div>
          <div className="space-y-1.5">
            {autoUpdates.map((u) => (
              <div key={u.id} className="flex items-start justify-between gap-3 text-sm border border-emerald-100 bg-emerald-50/40 rounded-lg px-3 py-2" data-testid={`ca-update-auto-${u.field}`}>
                <div className="min-w-0"><span className="font-medium text-slate-700">{u.label}:</span> <span className="text-slate-400 line-through mr-1">{fmtVal(u.existing_value)}</span><span className="text-slate-800">{fmtVal(u.proposed_value)}</span>
                  {u.excerpt && <div className="text-xs text-slate-400 italic mt-0.5 truncate">“{u.excerpt}”</div>}
                  <div className="text-[10px] text-slate-400 mt-0.5">Source: Meeting Analysis · applied automatically</div>
                </div>
                <span className="text-[10px] font-semibold uppercase text-emerald-600 shrink-0">Auto · {u.confidence}%</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {proposedUpdates.length > 0 && (
        <div data-testid="ca-updates-proposed">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-amber-600 mb-2 flex items-center gap-1.5"><Icons.AlertTriangle className="h-3.5 w-3.5" />Proposed High-Risk Updates ({pendingUpdates.length} pending)</div>
          <div className="space-y-2">
            {proposedUpdates.map((u) => {
              const resolved = u.status === "accepted" || u.status === "rejected";
              return (
                <div key={u.id} className={`border rounded-lg px-3 py-2.5 ${u.status === "accepted" ? "border-emerald-200 bg-emerald-50/40" : u.status === "rejected" ? "border-slate-200 bg-slate-50 opacity-70" : "border-amber-200 bg-amber-50/40"}`} data-testid={`ca-update-proposed-${u.field}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className="text-sm font-medium text-slate-800">{u.label}</span>
                    <span className="text-[10px] font-semibold uppercase text-amber-700 bg-amber-100 rounded px-1.5 py-0.5">{reasonLabel[u.reason] || "Review"} · {u.confidence}%</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm mt-1.5 flex-wrap">
                    <span className="text-slate-400 line-through">{fmtVal(u.existing_value)}</span>
                    <Icons.ArrowRight className="h-3.5 w-3.5 text-slate-400" />
                    <span className="font-semibold text-slate-800">{fmtVal(u.status === "accepted" ? u.applied_value : u.proposed_value)}</span>
                  </div>
                  {u.excerpt && <div className="text-xs text-slate-400 italic mt-1">“{u.excerpt}”</div>}
                  {resolved ? (
                    <div className={`text-[11px] font-semibold mt-2 ${u.status === "accepted" ? "text-emerald-600" : "text-slate-400"}`}>{u.status === "accepted" ? "✓ Accepted & applied" : "✕ Dismissed"}</div>
                  ) : (
                    <div className="flex items-center gap-2 mt-2.5">
                      <button onClick={() => actions.onAccept(u)} data-testid={`ca-update-accept-${u.field}`} className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 text-xs font-semibold"><Icons.Check className="h-3.5 w-3.5" />Accept{typeof u.proposed_value !== "boolean" ? " / Edit" : ""}</button>
                      <button onClick={() => actions.onReject(u)} data-testid={`ca-update-reject-${u.field}`} className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 text-slate-600 hover:bg-slate-50 px-3 py-1.5 text-xs font-semibold"><Icons.X className="h-3.5 w-3.5" />Reject</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
